import * as XLSX from 'xlsx';

// Generate estimate number
export function generateEstimateNumber() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const random = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
  return `INS-${year}-${month}${day}-${random}`;
}

// Format currency
function formatCurrency(amount) {
  return `$${(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Generate XLS export
export async function generateXLS(estimate) {
  const wb = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ['SNSHN Insurance Loss Estimate'],
    [],
    ['Property:', estimate.propertyAddress || 'N/A'],
    ['Date:', new Date().toLocaleDateString()],
    ['Estimate #:', generateEstimateNumber()],
    ['Version:', estimate.currentVersion || 1],
    [],
    ['SUMMARY'],
    ['Mitigation Subtotal:', formatCurrency(estimate.totals?.mitigation)],
    ['Repairs Subtotal:', formatCurrency(estimate.totals?.repairs)],
    ['Subtotal:', formatCurrency(estimate.totals?.subtotal)],
    ['O&P (20%):', formatCurrency(estimate.totals?.oAndP)],
    ['GRAND TOTAL:', formatCurrency(estimate.totals?.grandTotal)],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

  // Mitigation sheet
  const mitigationHeaders = ['Description', 'Qty', 'Unit', 'Rate', 'Total', 'Labor %'];
  const mitigationRows = (estimate.mitigation?.lineItems || []).map(item => [
    item.description,
    item.qty,
    item.unit,
    formatCurrency(item.rate),
    formatCurrency(item.total),
    `${item.laborPct}%`,
  ]);
  const mitigationData = [
    ['MITIGATION SCOPE'],
    [],
    mitigationHeaders,
    ...mitigationRows,
    [],
    ['Mitigation Subtotal:', '', '', '', formatCurrency(estimate.mitigation?.subtotal)],
  ];
  const mitigationSheet = XLSX.utils.aoa_to_sheet(mitigationData);
  XLSX.utils.book_append_sheet(wb, mitigationSheet, 'Mitigation');

  // Repairs sheet
  const repairsHeaders = ['Line', 'Description', 'Qty', 'Unit', 'Rate', 'Amount', 'Trade', 'Labor %', 'Labor $', 'Material $'];
  const repairsRows = (estimate.repairs?.items || []).map(item => {
    // Use estimatedAmount (from AI analysis) falling back to originalAmount or amount
    const amount = item.estimatedAmount || item.originalAmount || item.amount || 0;
    const laborAmt = (amount * item.laborPct / 100);
    const materialAmt = amount - laborAmt;
    return [
      item.sourceRef,
      item.description,
      item.qty || '',
      item.unit || '',
      item.rate ? formatCurrency(item.rate) : '',
      formatCurrency(amount),
      item.trade,
      `${item.laborPct}%`,
      formatCurrency(laborAmt),
      formatCurrency(materialAmt),
    ];
  });
  const repairsData = [
    ['REPAIRS'],
    [],
    repairsHeaders,
    ...repairsRows,
    [],
    ['Repairs Subtotal:', '', formatCurrency(estimate.repairs?.subtotal)],
    ['Labor Total:', '', formatCurrency(estimate.repairs?.laborTotal)],
    ['Material Total:', '', formatCurrency(estimate.repairs?.materialTotal)],
  ];
  const repairsSheet = XLSX.utils.aoa_to_sheet(repairsData);
  XLSX.utils.book_append_sheet(wb, repairsSheet, 'Repairs');

  // Generate file
  const filename = `${estimate.estimateName || 'Insurance-Estimate'}-v${estimate.currentVersion || 1}.xlsx`;
  XLSX.writeFile(wb, filename);
  return filename;
}

// Generate PDF export (using browser's print functionality for now)
// TODO: Replace with jsPDF for proper PDF generation
export async function generatePDF(estimate) {
  // Create a printable HTML document
  const estimateNumber = generateEstimateNumber();
  const html = `
<!DOCTYPE html>
<html>
<head>
  <title>Insurance Loss Estimate - ${estimate.estimateName || 'Estimate'}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #333; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { text-align: center; margin-bottom: 30px; border-bottom: 2px solid #E2FF2E; padding-bottom: 20px; }
    .header h1 { font-size: 24px; margin-bottom: 5px; }
    .header h2 { font-size: 14px; color: #666; font-weight: normal; }
    .info { margin-bottom: 20px; }
    .info-row { display: flex; margin-bottom: 5px; }
    .info-label { font-weight: bold; width: 120px; }
    .section { margin-bottom: 30px; }
    .section-title { font-size: 14px; font-weight: bold; background: #f5f5f5; padding: 8px; margin-bottom: 10px; border-left: 4px solid #E2FF2E; }
    table { width: 100%; border-collapse: collapse; }
    th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
    th { background: #f9f9f9; font-weight: bold; }
    .amount { text-align: right; }
    .total-row { font-weight: bold; background: #f5f5f5; }
    .grand-total { font-size: 16px; background: #E2FF2E; }
    .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 20px; }
    @media print { body { padding: 20px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>SNSHN</h1>
    <h2>INSURANCE LOSS ESTIMATE</h2>
  </div>

  <div class="info">
    <div class="info-row"><span class="info-label">Property:</span><span>${estimate.propertyAddress || 'N/A'}</span></div>
    <div class="info-row"><span class="info-label">Date:</span><span>${new Date().toLocaleDateString()}</span></div>
    <div class="info-row"><span class="info-label">Estimate #:</span><span>${estimateNumber}</span></div>
    <div class="info-row"><span class="info-label">Version:</span><span>${estimate.currentVersion || 1}</span></div>
  </div>

  <div class="section">
    <div class="section-title">SECTION 1: MITIGATION</div>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th class="amount">Qty</th>
          <th>Unit</th>
          <th class="amount">Rate</th>
          <th class="amount">Total</th>
        </tr>
      </thead>
      <tbody>
        ${(estimate.mitigation?.lineItems || []).map(item => `
        <tr>
          <td>${item.description}</td>
          <td class="amount">${item.qty}</td>
          <td>${item.unit}</td>
          <td class="amount">${formatCurrency(item.rate)}</td>
          <td class="amount">${formatCurrency(item.total)}</td>
        </tr>
        `).join('')}
        <tr class="total-row">
          <td colspan="4">MITIGATION SUBTOTAL</td>
          <td class="amount">${formatCurrency(estimate.mitigation?.subtotal)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">SECTION 2: REPAIRS</div>
    <table>
      <thead>
        <tr>
          <th>Description</th>
          <th class="amount">Qty</th>
          <th>Unit</th>
          <th class="amount">Rate</th>
          <th class="amount">Total</th>
          <th class="amount">Labor</th>
          <th class="amount">Material</th>
        </tr>
      </thead>
      <tbody>
        ${(estimate.repairs?.items || []).map(item => {
          // Use estimatedAmount (from AI analysis) falling back to originalAmount or amount
          const amount = item.estimatedAmount || item.originalAmount || item.amount || 0;
          const laborAmt = (amount * item.laborPct / 100);
          const materialAmt = amount - laborAmt;
          return `
        <tr>
          <td>${item.description}</td>
          <td class="amount">${item.qty || '-'}</td>
          <td>${item.unit || '-'}</td>
          <td class="amount">${item.rate ? formatCurrency(item.rate) : '-'}</td>
          <td class="amount">${formatCurrency(amount)}</td>
          <td class="amount">${formatCurrency(laborAmt)}</td>
          <td class="amount">${formatCurrency(materialAmt)}</td>
        </tr>
          `;
        }).join('')}
        <tr class="total-row">
          <td colspan="4">REPAIRS SUBTOTAL</td>
          <td class="amount">${formatCurrency(estimate.repairs?.subtotal)}</td>
          <td class="amount">${formatCurrency(estimate.repairs?.laborTotal)}</td>
          <td class="amount">${formatCurrency(estimate.repairs?.materialTotal)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="section">
    <div class="section-title">SUMMARY</div>
    <table>
      <tbody>
        <tr>
          <td>Mitigation</td>
          <td class="amount">${formatCurrency(estimate.totals?.mitigation)}</td>
        </tr>
        <tr>
          <td>Repairs</td>
          <td class="amount">${formatCurrency(estimate.totals?.repairs)}</td>
        </tr>
        <tr class="total-row">
          <td>Subtotal</td>
          <td class="amount">${formatCurrency(estimate.totals?.subtotal)}</td>
        </tr>
        <tr>
          <td>Overhead & Profit (20%)</td>
          <td class="amount">${formatCurrency(estimate.totals?.oAndP)}</td>
        </tr>
        <tr class="total-row grand-total">
          <td>TOTAL</td>
          <td class="amount">${formatCurrency(estimate.totals?.grandTotal)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="footer">
    <p>Generated: ${new Date().toLocaleString()} | Version ${estimate.currentVersion || 1} | ${estimateNumber}</p>
  </div>
</body>
</html>
  `;

  // Open in new window for printing/saving as PDF
  const printWindow = window.open('', '_blank');
  printWindow.document.write(html);
  printWindow.document.close();

  // Trigger print dialog (user can save as PDF from there)
  setTimeout(() => {
    printWindow.print();
  }, 500);

  return `${estimate.estimateName || 'Insurance-Estimate'}-v${estimate.currentVersion || 1}.pdf`;
}

// Generate both PDF and XLS
export async function generateExports(estimate) {
  const pdfName = await generatePDF(estimate);
  const xlsName = await generateXLS(estimate);
  return { pdfName, xlsName };
}
